import { CSSInterpolation } from '@emotion/css/create-instance'
import { css, cx } from '@emotion/css'
import { range } from 'lodash'
import React from 'react'

import type { FixedLengthArray } from '../../../types'
import { errorInDev } from '../../../util/logging'
import { typeSafeObjectEntries } from '../../../util/typeSafeObjectEntries'
import { typeSafeObjectKeys } from '../../../util/typeSafeObjectKeys'
import { typeSafeObjectValues } from '../../../util/typeSafeObjectValues'

/*
 * Layout System Glossary
 *
 * column: a standard unit of horizontal space in a layout system
 * gutter: the space in-between columns
 * margin: the space on the left and right side of the grid
 *
 * For example, a four-column layout system would looks like this:
 * ```
 *        |                          |        |                          |
 * margin |                          |        |                          | margin
 *        |         two columns      | gutter |        two columns       |
 *        |                          |        |                          |
 *        |                          |        |                          |
 *        |---------------------------        ---------------------------|
 *        |                                                              |
 *        |---------        ----------------------------        ---------|
 *        |        |        |                          |        |        |
 *        |        |        |                          |        |        |
 *        | column | gutter |        two columns       | gutter | column |
 *        |        |        |                          |        |        |
 *        |        |        |                          |        |        |
 * ```
 * Notice that the width of multi-columns containers also encompasses the
 *   gutter space in-between the columns.
 */

// NOTE(yan): layout size values are a bitmap so that layout range can be
//  a bit or of two or more layout sizes. this also allows comparison of
//  two layout sizes with math operators.
export const LAYOUT_SIZE = {
  /* eslint-disable no-bitwise */
  LG: (1 << 2) as 4,
  MD: (1 << 1) as 2,
  SM: (1 << 0) as 1,
  XL: (1 << 3) as 8,
  /* eslint-enable no-bitwise */
} as const

export type LayoutSizeKey = keyof typeof LAYOUT_SIZE
export type LayoutSize = (typeof LAYOUT_SIZE)[LayoutSizeKey]

// NOTE(yan): purposely ordered from smallest to largest layout sizes
//  exported for test purposes only
export const ORDERED_LAYOUT_SIZES = [
  LAYOUT_SIZE.SM,
  LAYOUT_SIZE.MD,
  LAYOUT_SIZE.LG,
  LAYOUT_SIZE.XL,
] as const

const LAYOUT_EXTRA_LARGE = {
  columnCount: 24,
  gutter: 24,
  margin: 24,
  widthMax: 2560,
  widthMin: 1441,
} as const
const LAYOUT_LARGE = {
  columnCount: 16,
  gutter: 24,
  margin: 24,
  widthMax: 1440,
  widthMin: 1049,
} as const
const LAYOUT_MEDIUM = {
  columnCount: 8,
  gutter: 24,
  margin: 24,
  widthMax: 1048,
  widthMin: 485,
} as const
const LAYOUT_SMALL = {
  columnCount: 2,
  gutter: 16,
  margin: 24,
  widthMax: 484,
  widthMin: 320,
} as const

export const LAYOUT_SPECS = {
  [LAYOUT_SIZE.SM]: LAYOUT_SMALL,
  [LAYOUT_SIZE.MD]: LAYOUT_MEDIUM,
  [LAYOUT_SIZE.LG]: LAYOUT_LARGE,
  [LAYOUT_SIZE.XL]: LAYOUT_EXTRA_LARGE,
} as const

type LayoutSpec = (typeof LAYOUT_SPECS)[LayoutSize]

// NOTE(yan):
//  LAYOUT_RANGE values are a bitmap that's the bitwise union of the two
//   two LAYOUT_SIZE values that begins and ends the range inclusively.
//  LayoutRoot constructs a range from only the end values, and tests it
//   against this map. So, do not include values in between the ends of the
//   range when declaring ranges.
//  Exported only for testing.
export const LAYOUT_RANGE = {
  ...LAYOUT_SIZE,
  /* eslint-disable no-bitwise */
  ALL: (LAYOUT_SIZE.SM | LAYOUT_SIZE.XL) as 9,
  LG_XL: (LAYOUT_SIZE.LG | LAYOUT_SIZE.XL) as 12,
  MD_LG: (LAYOUT_SIZE.MD | LAYOUT_SIZE.LG) as 6,
  MD_XL: (LAYOUT_SIZE.MD | LAYOUT_SIZE.XL) as 10,
  SM_LG: (LAYOUT_SIZE.SM | LAYOUT_SIZE.LG) as 5,
  SM_MD: (LAYOUT_SIZE.SM | LAYOUT_SIZE.MD) as 3,
  /* eslint-enable no-bitwise */
} as const

type LayoutRange = (typeof LAYOUT_RANGE)[keyof typeof LAYOUT_RANGE]

//  Exported only for testing
export function getLayoutSizeMinMaxFromLayoutRange(
  layoutRange: LayoutRange
): [LayoutSize, LayoutSize] {
  return [
    /* eslint-disable no-bitwise */
    (1 << Math.log2(layoutRange & -layoutRange)) as LayoutSize,
    (1 << Math.floor(Math.log2(layoutRange))) as LayoutSize,
    /* eslint-enable no-bitwise */
  ]
}

type LayoutSMColumns = 0 | 1 | 2
type LayoutMDColumns = LayoutSMColumns | 3 | 4 | 5 | 6 | 7 | 8
type LayoutLGColumns = LayoutMDColumns | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16
type LayoutXLColumns = LayoutLGColumns | 17 | 18 | 19 | 20 | 21 | 22 | 23 | 24

type ColumnsForLayout<T extends LayoutSize> = T extends typeof LAYOUT_SIZE.SM
  ? LayoutSMColumns
  : T extends typeof LAYOUT_SIZE.MD
  ? LayoutMDColumns
  : T extends typeof LAYOUT_SIZE.LG
  ? LayoutLGColumns
  : LayoutXLColumns

// TODO(yan): evaluate this at build/compile time
const COLUMNS_COUNT_FOR_LAYOUT_SIZE = typeSafeObjectEntries(
  LAYOUT_SPECS
).reduce(
  (accumulator, [layoutSize, layout]) => ({
    ...accumulator,
    [layoutSize]: layout.columnCount,
  }),
  {} as { [k in LayoutSize]: ColumnsForLayout<k> }
)

type ColumnsCountForLayoutSize<T extends LayoutSize> =
  T extends typeof LAYOUT_SIZE.SM
    ? typeof LAYOUT_SMALL.columnCount
    : T extends typeof LAYOUT_SIZE.MD
    ? typeof LAYOUT_MEDIUM.columnCount
    : T extends typeof LAYOUT_SIZE.LG
    ? typeof LAYOUT_LARGE.columnCount
    : typeof LAYOUT_EXTRA_LARGE.columnCount

// TODO(yan): evaluate this at build/compile time
const LAYOUT_COLUMN_WIDTHS_MAP = typeSafeObjectEntries(LAYOUT_SPECS).reduce(
  (accumulator, [layoutSize, layout]) => ({
    ...accumulator,
    [layoutSize]: range(0, layout.columnCount + 1).map(encompassingColumns =>
      range(0, layout.columnCount + 1).map(columns =>
        calculateLayoutColumnWidth(layout, encompassingColumns, columns)
      )
    ),
  }),
  {} as {
    [key in LayoutSize]: FixedLengthArray<
      FixedLengthArray<string, ColumnsCountForLayoutSize<key>>,
      ColumnsCountForLayoutSize<key>
    >
  }
)

function calculateLayoutColumnWidth(
  layout: LayoutSpec,
  encompassingColumns: number,
  columns: number
): string {
  if (columns === 0) {
    return '0px'
  }
  if (columns >= encompassingColumns) {
    // take up the full width when the non-zero columns requested matches or
    //  exceeds the amount of encompassing (available) columns
    return `calc(100% - ${layout.gutter}px)`
  }
  // NOTE(yan):
  //  column pixel width is calculated based on this formula:
  //    width = 100% * (c / ec) - (1 - c / ec) * g
  //          = (100% + g)(c / ec) - g
  //    where g   = gutter width in pixels
  //          c   = amount of columns columns for this container
  //          ec  = amount of encompassing (available) columns for this container
  //  since the encompassing columns has an additional half gutter space on each
  //    side baked in (see `getWrappingNestedColumnsStylesForLayoutSize`, in
  //    particular the `width` style value). then additional adjustment for the
  //    gutter space in the formula can be cancelled out.
  //  thereby, the final formula is:
  //    width = 100% * (c / ec) - g
  return `calc(100% * (${columns} / ${encompassingColumns}) - ${layout.gutter}px)`
}

// TODO(yan): evaluate this a build/compile time
const MEDIA_QUERY_FOR_LAYOUT_SIZE = typeSafeObjectEntries(LAYOUT_SPECS).reduce(
  (accumulator, [layoutSize, layout]) => ({
    ...accumulator,
    [layoutSize]: `@media (min-width: ${layout.widthMin}px)`,
  }),
  {} as { [key in LayoutSize]: string }
)

export const mediaQueryForLayoutSizeXLOrLarger =
  MEDIA_QUERY_FOR_LAYOUT_SIZE[LAYOUT_SIZE.XL]
export const mediaQueryForLayoutSizeLGOrLarger =
  MEDIA_QUERY_FOR_LAYOUT_SIZE[LAYOUT_SIZE.LG]
export const mediaQueryForLayoutSizeMDOrLarger =
  MEDIA_QUERY_FOR_LAYOUT_SIZE[LAYOUT_SIZE.MD]
// NOTE(yan) can't use the min-width for this since the selector needs to cover
//  all screen widths below the max
// TODO(yan): remove after refactoring the use of this selector to use mobile
//  styles by default
export const mediaQueryForLayoutSizeMDOrSmaller = `@media (max-width: ${
  LAYOUT_SPECS[LAYOUT_SIZE.MD].widthMax
}px)`

// TODO(yan): evaluate this at build/compile time, but double check `css()` can
//  be called at build time
const LAYOUT_ROOT_STYLES_FOR_LAYOUT_RANGE = typeSafeObjectValues(
  LAYOUT_RANGE
).reduce(
  (accumulator, layoutRange) => ({
    ...accumulator,
    [layoutRange]: cx(
      ...ORDERED_LAYOUT_SIZES.map(layoutSize =>
        getLayoutRootStyles(layoutRange, layoutSize)
      )
    ),
  }),
  {} as { [k in LayoutRange]: string }
)

function getLayoutRootStyles(layoutRange: LayoutRange, layoutSize: LayoutSize) {
  const [minLayoutSize, maxLayoutSize] =
    getLayoutSizeMinMaxFromLayoutRange(layoutRange)
  if (layoutSize < minLayoutSize || layoutSize > maxLayoutSize) {
    return null
  }

  const layout = LAYOUT_SPECS[layoutSize]
  const { margin, widthMax, widthMin } = layout
  const styles: CSSInterpolation = {
    boxSizing: 'border-box',
    // NOTE(yan): `margin` centers content after max width on largest layout
    margin: '0 auto',
    // NOTE(yan): max-width won't be in effect except for the LG layout
    //   since on smaller ones once the max-width is hit, it bumps up to
    //   the next layout.
    maxWidth: widthMax,
    minWidth: widthMin,
    paddingLeft: margin,
    paddingRight: margin,
    width: '100%',
  }
  const mediaQuery = MEDIA_QUERY_FOR_LAYOUT_SIZE[layoutSize]
  return css(layoutSize > minLayoutSize ? { [mediaQuery]: styles } : styles)
}

const SHARED_WRAPPING_NESTED_COLUMNS_STYLES = css({
  alignItems: 'flex-start',
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'flex-start',
  width: '100%',
})

// TODO(yan): evaluate this at build/compile time, but double check `css()`
//  call in the SHARED_WRAPPING_NESTED_COLUMNS_STYLES can run at build time

const WRAPPING_NESTED_COLUMNS_STYLES_FOR_LAYOUT_RANGE = typeSafeObjectValues(
  LAYOUT_RANGE
).reduce(
  (accumulator, layoutRange) => ({
    ...accumulator,
    [layoutRange]: cx(
      SHARED_WRAPPING_NESTED_COLUMNS_STYLES,
      ...ORDERED_LAYOUT_SIZES.map(layoutSize =>
        getWrappingNestedColumnsStyles(layoutRange, layoutSize)
      )
    ),
  }),
  {} as { [k in LayoutRange]: string }
)

function getWrappingNestedColumnsStyles(
  layoutRange: LayoutRange,
  layoutSize: LayoutSize
) {
  const [minLayoutSize, maxLayoutSize] =
    getLayoutSizeMinMaxFromLayoutRange(layoutRange)
  if (layoutSize < minLayoutSize || layoutSize > maxLayoutSize) {
    return null
  }

  const layout = LAYOUT_SPECS[layoutSize]
  const { gutter } = layout
  const styles: CSSInterpolation = {
    marginLeft: -(gutter / 2),
    marginRight: -(gutter / 2),
    width: `calc(100% + ${gutter}px)`,
  }
  const mediaQuery = MEDIA_QUERY_FOR_LAYOUT_SIZE[layoutSize]
  return css(layoutSize > minLayoutSize ? { [mediaQuery]: styles } : styles)
}

const LayoutRootRangeContext = React.createContext<LayoutRange>(
  LAYOUT_RANGE.ALL
)

type LayoutRootProps = {
  /**
   * UI to be displayed within the layout system
   */
  children: React.ReactNode
  /**
   * (Optional) specify override styles on the root element. Please do this sparingly.
   *
   * NOTE: there's two nested `div`. If you need to target styles for the inner one use selector `> div`
   */
  className?: string
  /**
   * (Optional) specify root element.
   *
   * NOTE: some elements have default styles that may conflict with css applied internally for this component to work
   */
  dangerouslyOverrideRootElement?: keyof React.JSX.IntrinsicElements
  /**
   * (Optional) specify the maximum layout size support within this layout root.
   */
  maxLayoutSize?: LayoutSizeKey
  /**
   * (Optional) specify the minimum layout size support within this layout root.
   */
  minLayoutSize?: LayoutSizeKey
}

/**
 * The top level layout component that underpins all decendent layout components.
 *
 * This component must rendered full bleed (horizontally edge-to-edge) on the page. Meaning no additional horizontal `border` / `padding` / `margin` / `transforms` styles from any parent element, or added to itself.
 *
 * Other content can be placed above or below it on the flow, and multiple `LayoutRoot` components can be on the same page.
 */
export function LayoutRoot({
  children,
  className,
  dangerouslyOverrideRootElement: RootElement = 'div',
  maxLayoutSize = 'LG',
  minLayoutSize = 'SM',
}: LayoutRootProps) {
  const layoutRange = React.useMemo(() => {
    const possibleRange =
      // eslint-disable-next-line no-bitwise
      LAYOUT_SIZE[minLayoutSize] | LAYOUT_SIZE[maxLayoutSize]
    const rangeKey = typeSafeObjectKeys(LAYOUT_RANGE).find(
      k => LAYOUT_RANGE[k] === possibleRange
    )
    if (!rangeKey) {
      errorInDev(
        `LayoutRoot received an invalid range: minLayoutSize='${minLayoutSize}' maxLayoutSize='${maxLayoutSize}'. defaulting to LAYOUT_RANGE.ALL`
      )
      return LAYOUT_RANGE.ALL
    }
    return LAYOUT_RANGE[rangeKey]
  }, [minLayoutSize, maxLayoutSize])
  return (
    <LayoutRootRangeContext.Provider value={layoutRange}>
      <RootElement
        className={cx(
          LAYOUT_ROOT_STYLES_FOR_LAYOUT_RANGE[layoutRange],
          className
        )}
      >
        <div
          className={
            WRAPPING_NESTED_COLUMNS_STYLES_FOR_LAYOUT_RANGE[layoutRange]
          }
        >
          {children}
        </div>
      </RootElement>
    </LayoutRootRangeContext.Provider>
  )
}

type BaseColumnProps = {
  children?: React.ReactNode
  className?: string
  hasNestedContainers?: boolean
  dangerouslyOverrideRootElement?: keyof React.JSX.IntrinsicElements
  id?: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >['id']
}

type ContainerColumnsProps = {
  columnsLG: LayoutLGColumns
  columnsMD: LayoutMDColumns
  columnsSM: LayoutSMColumns
  columnsXL: LayoutXLColumns
}

type LayoutColumnProps = BaseColumnProps & ContainerColumnsProps

type LayoutColumnEncompassingColumnsContextValue = {
  [k in LayoutSize]: ColumnsForLayout<k>
}

const LayoutColumnEncompassingColumnsContext =
  React.createContext<LayoutColumnEncompassingColumnsContextValue>(
    // default context value is for LayoutColumn components that are direct
    //  children of LayoutRoot, which means they can use all the columns
    COLUMNS_COUNT_FOR_LAYOUT_SIZE
  )

function LayoutColumn({
  children,
  className,
  columnsLG,
  columnsMD,
  columnsSM,
  columnsXL,
  dangerouslyOverrideRootElement: RootElement = 'div',
  hasNestedContainers,
  id,
}: LayoutColumnProps) {
  const layoutRange = React.useContext(LayoutRootRangeContext)
  const encompassingColumnsForLayoutSize = React.useContext(
    LayoutColumnEncompassingColumnsContext
  )
  const columnsForLayoutSize = React.useMemo(
    () => ({
      [LAYOUT_SIZE.LG]: columnsLG,
      [LAYOUT_SIZE.MD]: columnsMD,
      [LAYOUT_SIZE.SM]: columnsSM,
      [LAYOUT_SIZE.XL]: columnsXL,
    }),
    [columnsLG, columnsMD, columnsSM, columnsXL]
  )

  const columnStyles = React.useMemo(
    () =>
      css([
        ...ORDERED_LAYOUT_SIZES.map(layoutSize =>
          getLayoutColumnStyles(
            layoutRange,
            layoutSize,
            encompassingColumnsForLayoutSize,
            columnsForLayoutSize
          )
        ),
      ]),
    [columnsForLayoutSize, encompassingColumnsForLayoutSize, layoutRange]
  )
  const rootStyles = cx(columnStyles, className)
  return hasNestedContainers ? (
    <LayoutColumnEncompassingColumnsContext.Provider
      value={columnsForLayoutSize}
    >
      <RootElement className={rootStyles} id={id}>
        <div
          className={
            WRAPPING_NESTED_COLUMNS_STYLES_FOR_LAYOUT_RANGE[layoutRange]
          }
        >
          {children}
        </div>
      </RootElement>
    </LayoutColumnEncompassingColumnsContext.Provider>
  ) : (
    <RootElement className={rootStyles} id={id}>
      {children}
    </RootElement>
  )
}

function getLayoutColumnStyles(
  layoutRange: LayoutRange,
  layoutSize: LayoutSize,
  encompassingColumnsForLayoutSize: LayoutColumnEncompassingColumnsContextValue,
  columnsForLayoutSize: LayoutColumnEncompassingColumnsContextValue
) {
  const [minLayoutSize, maxLayoutSize] =
    getLayoutSizeMinMaxFromLayoutRange(layoutRange)
  if (layoutSize < minLayoutSize || layoutSize > maxLayoutSize) {
    return null
  }

  const layout = LAYOUT_SPECS[layoutSize]

  // determine the width of the component based on number of columns
  const encompassingColumns = encompassingColumnsForLayoutSize[layoutSize]
  const columns = columnsForLayoutSize[layoutSize]
  const width =
    LAYOUT_COLUMN_WIDTHS_MAP[layoutSize][encompassingColumns][columns]

  // zero columns content needs to be properly hidden
  const isZeroColumns = columns === 0

  const styles: CSSInterpolation = {
    display: isZeroColumns ? 'none' : 'initial',
    flexBasis: width,
    flexGrow: 0,
    flexShrink: 0,
    marginLeft: layout.gutter / 2,
    marginRight: layout.gutter / 2,
    visibility: isZeroColumns ? 'hidden' : 'visible',
    width,
  }
  const mediaQuery = MEDIA_QUERY_FOR_LAYOUT_SIZE[layoutSize]
  return layoutSize > minLayoutSize ? { [mediaQuery]: styles } : styles
}

/**
 * There are five `Container` span sizes:
 *
 * - A `full`-span `Container` will span the **entire** _encompassing columns space_<sup>†</sup>
 * - A `threeQuarter`-span `Container` will attempt to span **three-fourth** of the _encompassing columns space_
 * - A `half`-span `Container` will attempt to span **half** the _encompassing columns space_
 * - A `quarter`-span `Container` will attempt to span a **quarter** of the _encompassing columns space_
 * - A `zero`-span `Container` will take up **none** of the _encompassing columns space_
 *
 * † The **encompassing columns space** is the full number of columns on the page, (e.g. 16 on the Large LayoutSize for desktop), or the number of columns the parent `Container` encompasses for a nested child `Container`.
 */
export const CONTAINERS_SPAN_TO_FRACTIONS = {
  full: 1.0,
  half: 0.5,
  quarter: 0.25,
  threeQuarter: 0.75,
  zero: 0,
} as const

type ContainerSpans = keyof typeof CONTAINERS_SPAN_TO_FRACTIONS

type ContainerProps = BaseColumnProps & {
  spanLG?: ContainerSpans
  spanMD?: ContainerSpans
  spanSM?: ContainerSpans
  spanXL?: ContainerSpans
}

/**
 * `Container`-s are a higher level abstraction built on top of the grid system.
 *
 * We built `Container`-s to help you build responsive layouts more easily. `Container`-s are **responsive** according to the current `LayoutSize`. They are **composable**, so you can combine them to express complex layouts. By using containers, you don't have to specify the exact column counts for each `LayoutSize` to build your UI.
 *
 * Simply put: you build UI within `Container`-s, and leave it up to the `Container`-s and Bento Layout System to size and place them in the best most responsive way.
 */
export function Container({
  children,
  className,
  dangerouslyOverrideRootElement,
  hasNestedContainers,
  spanLG,
  spanMD,
  spanSM = 'full',
  spanXL,
  ...props
}: ContainerProps) {
  const encompassingColumnsForLayoutSize =
    React.useContext(LayoutColumnEncompassingColumnsContext) ||
    COLUMNS_COUNT_FOR_LAYOUT_SIZE
  const encompassingColumnsSM = encompassingColumnsForLayoutSize[LAYOUT_SIZE.SM]
  const encompassingColumnsMD = encompassingColumnsForLayoutSize[LAYOUT_SIZE.MD]
  const encompassingColumnsLG = encompassingColumnsForLayoutSize[LAYOUT_SIZE.LG]
  const encompassingColumnsXL = encompassingColumnsForLayoutSize[LAYOUT_SIZE.XL]

  const adjustedSpanMD = spanMD || spanSM
  const adjustedSpanLG = spanLG || adjustedSpanMD
  const adjustedSpanXL = spanXL || adjustedSpanLG
  // NOTE(yan): memoize b/c different props inputed can yield the same columns
  const [columnsSM, columnsMD, columnsLG, columnsXL] = React.useMemo(() => {
    return [
      getFractionalContainerColumnsInLayout<typeof LAYOUT_SIZE.SM>(
        encompassingColumnsSM,
        spanSM
      ),
      getFractionalContainerColumnsInLayout<typeof LAYOUT_SIZE.MD>(
        encompassingColumnsMD,
        adjustedSpanMD
      ),
      getFractionalContainerColumnsInLayout<typeof LAYOUT_SIZE.LG>(
        encompassingColumnsLG,
        adjustedSpanLG
      ),
      getFractionalContainerColumnsInLayout<typeof LAYOUT_SIZE.XL>(
        encompassingColumnsXL,
        adjustedSpanXL
      ),
    ]
  }, [
    encompassingColumnsSM,
    encompassingColumnsMD,
    encompassingColumnsLG,
    encompassingColumnsXL,
    spanSM,
    adjustedSpanMD,
    adjustedSpanLG,
    adjustedSpanXL,
  ])
  return (
    <LayoutColumn
      className={className}
      columnsLG={columnsLG}
      columnsMD={columnsMD}
      columnsSM={columnsSM}
      columnsXL={columnsXL}
      dangerouslyOverrideRootElement={dangerouslyOverrideRootElement}
      hasNestedContainers={hasNestedContainers}
      {...props}
    >
      {children}
    </LayoutColumn>
  )
}

function getFractionalContainerColumnsInLayout<T extends LayoutSize>(
  columns: ColumnsForLayout<T>,
  span: ContainerSpans
): ColumnsForLayout<T> {
  const fraction = CONTAINERS_SPAN_TO_FRACTIONS[span]
  const preferredColumns = columns * fraction
  // NOTE(yan): since it's an integer between 0 up to `columns` we know it is
  //   safely a valid container column count. so cast it to make typescript
  //   happy.
  return (
    Number.isInteger(preferredColumns)
      ? parseInt(preferredColumns.toFixed(0), 10)
      : columns
  ) as ColumnsForLayout<T>
}

export const LayoutColumnWithRegrets = LayoutColumn

export function LayoutRowWithRegrets({
  children,
  className,
}: Omit<BaseColumnProps, 'hasNestedContainers'>) {
  return (
    <Container
      className={className}
      hasNestedContainers
      spanLG="full"
      spanMD="full"
      spanSM="full"
      spanXL="full"
    >
      {children}
    </Container>
  )
}
